import {
  downloadBlob,
  generatePdfBlobFromRenderedHtml
} from '~/components/common/dropdowns/DownloadDocumentDropdown'
import { BetterToast } from '~/utils/toast'
import { buildCanvasDownloadFilename } from './canvasDocumentMeta'

export const CANVAS_HTML_SANDBOX = 'allow-scripts allow-forms allow-popups allow-modals'

const SNAPSHOT_REQUEST = 'curie-canvas-pdf-snapshot-request'
const SNAPSHOT_RESPONSE = 'curie-canvas-pdf-snapshot'
const HEIGHT_RESPONSE = 'curie-canvas-html-height'
const DOWNLOAD_REQUEST = 'curie-canvas-pdf-download-request'
const DOWNLOAD_RESPONSE = 'curie-canvas-pdf-download-response'

interface CanvasHtmlMessage {
  type?: string
  token?: string
  html?: string
  height?: number
  error?: string
}

interface SandboxedCanvasWrapperOptions {
  downloadRequestToken?: string
  downloadTargetOrigin?: string
}

interface OpenSandboxedCanvasHtmlOptions {
  onDownloadPdf?: () => void | Promise<void>
}

interface RenderedCanvasHtmlPdfOptions {
  filename: string
  renderWidth?: number
}

interface CaptureRenderedCanvasHtmlOptions {
  renderWidth?: number
}

const DEFAULT_RENDERED_CANVAS_HTML_PDF_WIDTH = 1200
const OPEN_TAB_BRIDGE_MAX_LIFETIME_MS = 10 * 60 * 1000

function normalizeRenderWidth(width: number | undefined): number {
  if (!width || !Number.isFinite(width)) return DEFAULT_RENDERED_CANVAS_HTML_PDF_WIDTH
  return Math.round(Math.min(1600, Math.max(720, width)))
}

function createToken(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  return `curie-canvas-${Date.now()}-${random}`
}

function encodeUtf8Base64(value: string): string {
  const utf8 = new TextEncoder().encode(value)
  let binary = ''
  for (let i = 0; i < utf8.length; i++) {
    binary += String.fromCharCode(utf8[i])
  }
  return btoa(binary)
}

function jsonForInlineScript(value: string): string {
  return JSON.stringify(value).replace(/<\/script/gi, '<\\/script')
}

function buildSnapshotBridgeScript(token: string): string {
  return (
    '<script data-curie-canvas-bridge="true">(function(){' +
    'var token=' +
    JSON.stringify(token) +
    ';' +
    'function maxHeight(){var d=document.documentElement;var b=document.body;return Math.ceil(Math.max(' +
    'd?d.scrollHeight:0,d?d.offsetHeight:0,d?d.clientHeight:0,b?b.scrollHeight:0,b?b.offsetHeight:0,b?b.clientHeight:0));}' +
    'function snapshot(){var clone=document.documentElement.cloneNode(true);' +
    'var sourceCanvases=document.querySelectorAll("canvas");var cloneCanvases=clone.querySelectorAll("canvas");' +
    'for(var c=0;c<sourceCanvases.length;c++){try{var img=document.createElement("img");img.src=sourceCanvases[c].toDataURL("image/png");img.width=sourceCanvases[c].width;img.height=sourceCanvases[c].height;img.className=sourceCanvases[c].className;img.style.cssText=sourceCanvases[c].style.cssText;if(cloneCanvases[c])cloneCanvases[c].replaceWith(img);}catch(e){}}' +
    'var scripts=clone.querySelectorAll("script");for(var i=0;i<scripts.length;i++){scripts[i].remove();}' +
    'return "<!doctype html>\\n"+clone.outerHTML;}' +
    'function postHeight(){window.parent.postMessage({type:"' +
    HEIGHT_RESPONSE +
    '",token:token,height:maxHeight()},"*");}' +
    'window.addEventListener("message",function(event){var data=event.data||{};' +
    'if(data.token!==token)return;' +
    'if(data.type!=="' +
    SNAPSHOT_REQUEST +
    '")return;' +
    'try{window.parent.postMessage({type:"' +
    SNAPSHOT_RESPONSE +
    '",token:token,html:snapshot(),height:maxHeight()},"*");}' +
    'catch(error){window.parent.postMessage({type:"' +
    SNAPSHOT_RESPONSE +
    '",token:token,error:String(error&&error.message||error)},"*");}});' +
    'window.addEventListener("load",function(){setTimeout(postHeight,0);setTimeout(postHeight,250);setTimeout(postHeight,1000);});' +
    'if(window.ResizeObserver){var observer=new ResizeObserver(postHeight);observer.observe(document.documentElement);if(document.body)observer.observe(document.body);}' +
    'setTimeout(postHeight,0);' +
    '})();</script>'
  )
}

function appendBridgeScript(html: string, token: string): string {
  const bridgeScript = buildSnapshotBridgeScript(token)
  if (/<\/body\s*>/i.test(html)) {
    return html.replace(/<\/body\s*>/i, `${bridgeScript}</body>`)
  }
  return `${html}${bridgeScript}`
}

export function buildSandboxedCanvasWrapper(
  html: string,
  options: SandboxedCanvasWrapperOptions = {}
): string {
  const token = createToken()
  const downloadRequestToken = options.downloadRequestToken
  const downloadTargetOrigin = options.downloadTargetOrigin ?? window.location.origin
  const base64Payload = encodeUtf8Base64(html)
  const bridgeScript = buildSnapshotBridgeScript(token)
  const toolbarHtml = downloadRequestToken
    ? '<div id="canvas-toolbar"><button type="button" id="canvas-download-pdf">Download PDF</button></div>'
    : ''
  const toolbarStyle = downloadRequestToken
    ? '#canvas-toolbar{position:fixed;top:16px;right:16px;z-index:10;display:flex;gap:8px}' +
      '#canvas-toolbar button{height:36px;border:1px solid rgba(15,15,15,.14);border-radius:10px;background:#fff;padding:0 14px;font:600 13px system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;box-shadow:0 1px 2px rgba(15,15,15,.08);cursor:pointer;overflow:hidden;transition:all .3s ease}' +
      '#canvas-toolbar button:hover{background:#f7f7f7}' +
      '#canvas-toolbar button:disabled{cursor:wait}' +
      '#canvas-toolbar button.is-loading{height:16px;min-width:112px;border-color:rgba(99,102,241,.28);border-radius:999px;background:rgb(238,242,255);padding:0 8px;color:rgb(49,46,129);box-shadow:none}' +
      '#canvas-toolbar button.is-loading:hover{background:rgb(238,242,255)}' +
      '.canvas-download-progress{display:flex;width:100%;align-items:center;gap:8px}' +
      '.canvas-download-progress-track{position:relative;height:6px;min-width:64px;flex:1;overflow:hidden;border-radius:999px;background:rgb(224,231,255)}' +
      '.canvas-download-progress-fill{position:absolute;inset-block:0;left:0;border-radius:999px;background:linear-gradient(90deg,rgba(129,140,248,.7),rgba(167,139,250,.7),rgba(217,70,239,.7));box-shadow:inset -8px 0 12px -8px rgba(255,255,255,.6);transition:width .3s ease-out}' +
      '.canvas-download-progress-label{white-space:nowrap;font-size:11px;font-weight:600;line-height:1;font-variant-numeric:tabular-nums}'
    : ''
  const downloadButtonScript = downloadRequestToken
    ? 'var downloadButton=document.getElementById("canvas-download-pdf");' +
      'var downloadProgressTimer=null;' +
      'function renderDownloadProgress(progress){if(!downloadButton)return;var pct=Math.max(0,Math.min(100,Math.round(progress)));downloadButton.innerHTML="<span class=\\"canvas-download-progress\\"><span class=\\"canvas-download-progress-track\\"><span class=\\"canvas-download-progress-fill\\" style=\\"width:"+pct+"%\\"></span></span><span class=\\"canvas-download-progress-label\\">"+pct+"%</span></span>";}' +
      'function resetDownloadButton(){if(!downloadButton)return;if(downloadProgressTimer)window.clearInterval(downloadProgressTimer);downloadProgressTimer=null;downloadButton.classList.remove("is-loading");downloadButton.disabled=false;downloadButton.textContent="Download PDF";}' +
      'function startDownloadProgress(){if(!downloadButton)return;if(downloadProgressTimer)window.clearInterval(downloadProgressTimer);var progress=0;var startedAt=Date.now();downloadButton.classList.add("is-loading");downloadButton.disabled=true;renderDownloadProgress(progress);downloadProgressTimer=window.setInterval(function(){var elapsed=Date.now()-startedAt;var next=Math.round(95*(1-Math.exp(-elapsed/3500)));progress=Math.max(progress,Math.min(95,next));renderDownloadProgress(progress);},120);}' +
      'function completeDownloadProgress(){if(downloadProgressTimer)window.clearInterval(downloadProgressTimer);downloadProgressTimer=null;renderDownloadProgress(100);window.setTimeout(resetDownloadButton,300);}' +
      'downloadButton.addEventListener("click",function(){if(!window.opener){alert("Return to the canvas to download this PDF.");return;}startDownloadProgress();window.opener.postMessage({type:"' +
      DOWNLOAD_REQUEST +
      '",token:' +
      JSON.stringify(downloadRequestToken) +
      '},' +
      jsonForInlineScript(downloadTargetOrigin) +
      ');});' +
      'window.addEventListener("message",function(event){var data=event.data||{};if(data.type!=="' +
      DOWNLOAD_RESPONSE +
      '"||data.token!==' +
      JSON.stringify(downloadRequestToken) +
      ')return;if(data.error){resetDownloadButton();alert(data.error);}else{completeDownloadProgress();}});'
    : ''

  return (
    '<!doctype html>' +
    '<html lang="en">' +
    '<head>' +
    '<meta charset="utf-8">' +
    '<title>Canvas preview</title>' +
    '<style>' +
    'html,body{margin:0;padding:0;min-height:100%;background:#fff;color:#111;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}' +
    '#canvas-frame{width:100%;min-height:100vh;border:0;display:block;background:#fff}' +
    toolbarStyle +
    '</style>' +
    '</head>' +
    '<body>' +
    toolbarHtml +
    '<iframe id="canvas-frame" sandbox="' +
    CANVAS_HTML_SANDBOX +
    '" referrerpolicy="no-referrer"></iframe>' +
    '<script>(function(){' +
    'var token=' +
    JSON.stringify(token) +
    ';' +
    'var b64=' +
    JSON.stringify(base64Payload) +
    ';' +
    'var bridge=' +
    jsonForInlineScript(bridgeScript) +
    ';' +
    'var bin=atob(b64);' +
    'var bytes=new Uint8Array(bin.length);' +
    'for(var i=0;i<bin.length;i++){bytes[i]=bin.charCodeAt(i);}' +
    'var html=new TextDecoder("utf-8").decode(bytes);' +
    'function appendBridge(input){return /<\\/body\\s*>/i.test(input)?input.replace(/<\\/body\\s*>/i,bridge+"</body>"):input+bridge;}' +
    'var frame=document.getElementById("canvas-frame");' +
    'function setFrameHeight(height){if(!height)return;var px=Math.max(height,window.innerHeight);frame.style.height=px+"px";document.documentElement.style.setProperty("--curie-canvas-print-height",px+"px");}' +
    'window.addEventListener("message",function(event){if(event.source!==frame.contentWindow)return;var data=event.data||{};if(data.token!==token)return;if(data.type==="' +
    HEIGHT_RESPONSE +
    '")setFrameHeight(data.height);});' +
    downloadButtonScript +
    'frame.srcdoc=appendBridge(html);' +
    '})();</script>' +
    '</body></html>'
  )
}

export function openSandboxedCanvasHtmlInNewTab(
  html: string,
  options: OpenSandboxedCanvasHtmlOptions = {}
): boolean {
  const downloadRequestToken = options.onDownloadPdf ? createToken() : undefined
  const wrapper = buildSandboxedCanvasWrapper(html, {
    downloadRequestToken,
    downloadTargetOrigin: window.location.origin
  })
  const blob = new Blob([wrapper], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const opened = window.open('', '_blank')

  if (!opened) {
    URL.revokeObjectURL(url)
    return false
  }

  let cleanupDownloadListener: (() => void) | undefined
  if (downloadRequestToken && options.onDownloadPdf) {
    const handleDownloadRequest = (event: MessageEvent<CanvasHtmlMessage>) => {
      if (event.source !== opened) return
      const data = event.data || {}
      if (data.type !== DOWNLOAD_REQUEST || data.token !== downloadRequestToken) return

      Promise.resolve(options.onDownloadPdf?.())
        .then(() => {
          opened.postMessage(
            { type: DOWNLOAD_RESPONSE, token: downloadRequestToken },
            window.location.origin
          )
        })
        .catch(error => {
          console.error('PDF generation failed from canvas preview tab:', error)
          BetterToast.error('Failed to generate PDF')
          opened.postMessage(
            {
              type: DOWNLOAD_RESPONSE,
              token: downloadRequestToken,
              error: 'Failed to generate PDF'
            },
            window.location.origin
          )
        })
    }
    window.addEventListener('message', handleDownloadRequest)
    const closedPoll = window.setInterval(() => {
      if (!opened.closed) return
      cleanupDownloadListener?.()
    }, 1000)
    const bridgeLifetimeTimeout = window.setTimeout(() => {
      cleanupDownloadListener?.()
    }, OPEN_TAB_BRIDGE_MAX_LIFETIME_MS)
    const handlePageHide = () => cleanupDownloadListener?.()
    window.addEventListener('pagehide', handlePageHide, { once: true })
    cleanupDownloadListener = () => {
      window.removeEventListener('message', handleDownloadRequest)
      window.clearInterval(closedPoll)
      window.clearTimeout(bridgeLifetimeTimeout)
      window.removeEventListener('pagehide', handlePageHide)
      cleanupDownloadListener = undefined
    }
  } else {
    opened.opener = null
  }

  opened.location.href = url
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return true
}

export async function captureRenderedCanvasHtml(
  html: string,
  options: CaptureRenderedCanvasHtmlOptions = {},
  timeoutMs = 10_000
): Promise<string> {
  const token = createToken()
  const renderWidth = normalizeRenderWidth(options.renderWidth)
  const iframe = document.createElement('iframe')
  iframe.setAttribute('sandbox', CANVAS_HTML_SANDBOX)
  iframe.setAttribute('referrerpolicy', 'no-referrer')
  iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${renderWidth}px;height:900px;border:0;visibility:hidden;`

  return await new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const cleanup = () => {
      window.removeEventListener('message', handleMessage)
      if (timeoutId) clearTimeout(timeoutId)
      iframe.remove()
    }

    const requestSnapshot = () => {
      iframe.contentWindow?.postMessage({ type: SNAPSHOT_REQUEST, token }, '*')
    }

    const setCaptureFrameHeight = (height: number | undefined) => {
      if (!height || !Number.isFinite(height)) return
      iframe.style.height = `${Math.max(900, Math.ceil(height))}px`
    }

    const handleMessage = (event: MessageEvent<CanvasHtmlMessage>) => {
      if (event.source !== iframe.contentWindow) return
      if (event.origin !== 'null') return
      const data = event.data || {}
      if (data.token !== token) return
      if (data.type === HEIGHT_RESPONSE) {
        setCaptureFrameHeight(data.height)
        return
      }
      if (data.type !== SNAPSHOT_RESPONSE) return
      cleanup()
      if (data.error) {
        reject(new Error(data.error))
        return
      }
      resolve(data.html || html)
    }

    window.addEventListener('message', handleMessage)
    timeoutId = setTimeout(() => {
      cleanup()
      reject(new Error('Timed out while preparing rendered HTML for PDF'))
    }, timeoutMs)

    iframe.addEventListener('load', () => {
      setTimeout(requestSnapshot, 500)
    })

    document.body.appendChild(iframe)
    iframe.srcdoc = appendBridgeScript(html, token)
  })
}

export async function generateRenderedCanvasHtmlPdfBlob(
  html: string,
  options: Omit<RenderedCanvasHtmlPdfOptions, 'filename'> = {}
): Promise<Blob> {
  const renderWidth = normalizeRenderWidth(options.renderWidth)
  const renderedHtml = await captureRenderedCanvasHtml(html, { renderWidth })
  return generatePdfBlobFromRenderedHtml(renderedHtml, { renderWidth })
}

export async function downloadRenderedCanvasHtmlPdf(
  html: string,
  options: RenderedCanvasHtmlPdfOptions
): Promise<void> {
  const blob = await generateRenderedCanvasHtmlPdfBlob(html, { renderWidth: options.renderWidth })
  downloadBlob(blob, buildCanvasDownloadFilename(options.filename, { extension: 'pdf' }))
}
